import { Link } from "@components/cms/link/Link";
import type { ILink, ILinksCollection } from "@contentful-api/types/contentful";
import { resolveMediaAsset } from "@lib/contentful/api/helpers/resolveMediaAsset";
import { H } from "@ecco/ui/a11y-heading";
import { Text } from "@ecco/ui/text";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import type { SimpleGridProps } from "@ecco/ui/simple-grid";
import { SimpleGrid } from "@ecco/ui/simple-grid";
import { NextImage } from "@ecco/ui/next-image";
import { Carousel } from "@ecco/ui/carousel";
import type { FC } from "react";

interface ISearchCarouselsProps {
    customLinksCarousel: ILinksCollection[];
    variant?: "default" | "categories";
    categoryColumns?: SimpleGridProps["columns"];
}

const CategoryLink: FC<{ link: ILink }> = ({ link }) => {
    const resolvedImage = resolveMediaAsset(link?.fields?.linkAsset, link?.fields?.linkImage);
    const imgAlt = resolvedImage?.description || "";
    const imgSrc = resolvedImage?.url || "";

    return (
        <Link
            h="full"
            w="full"
            minW={0}
            link={link}
            color="black"
            textDecor="none"
            _hover={{ textDecoration: "none", color: "black" }}
        >
            <Flex h="full" flexDir="column" gap={2} minW={0}>
                <Box aspectRatio={1} w="full" overflow="hidden" position="relative">
                    <NextImage
                        alt={imgAlt}
                        src={imgSrc}
                        objectFit="cover"
                        sizes={["50vw", "25vw", "16vw"]}
                        fill
                    />
                </Box>
                <Text
                    color="black"
                    fontWeight="bold"
                    fontSize="mobileBodyTextLarge"
                    textDecoration="underline"
                    wordBreak="break-word"
                >
                    {link?.fields?.label}
                </Text>
            </Flex>
        </Link>
    );
};

const CategoriesGrid: FC<{
    title?: string;
    links: ILink[];
    columns?: SimpleGridProps["columns"];
}> = ({ title, links, columns = [2, 2, 4, 6] }) => (
    <Box>
        {title ? (
            <H as="h2" size="h4" mb={4} fontSize="mobileBodyTextLarge" fontWeight="bold">
                {title}
            </H>
        ) : null}
        <SimpleGrid
            columns={columns}
            spacingX={[1, 1, 3, 4]}
            spacingY={[1, 1, 3, 4]}
            w="100%"
            minW={0}
        >
            {links.map((link) => (
                <CategoryLink key={link.sys.id} link={link} />
            ))}
        </SimpleGrid>
    </Box>
);

const DefaultCarousel: FC<{
    linkCollection: ILinksCollection;
    links: ILink[];
    index: number;
}> = ({ linkCollection, links, index }) => (
    <Carousel
        gap={1}
        px={[2, 2, 2, 0]}
        pb={[2, 2, 4, 16]}
        pt={index === 0 ? [4, 4, 10, 24] : undefined}
        h={["8.5rem", "15rem", "15rem", "15rem", "15rem", "18rem"]}
        title={linkCollection?.fields?.title}
        titleAriaLabel={linkCollection?.fields?.title}
        showNavigation
        slidesPerView={[2.2, 3, 4]}
    >
        {links.map((link) => {
            const resolvedImage = resolveMediaAsset(
                link?.fields?.linkAsset,
                link?.fields?.linkImage
            );
            const imgAlt = resolvedImage?.description || "";
            const imgSrc = resolvedImage?.url || "";

            return (
                <Link
                    key={link.sys.id}
                    h="full"
                    w="full"
                    link={link}
                    color="black"
                    textDecor="none"
                    _hover={{ textDecoration: "none", color: "black" }}
                >
                    <Flex h="full" flexDir="column" gap={2}>
                        <NextImage alt={imgAlt} src={imgSrc} objectFit="cover" sizes={["50vw"]} />
                        <Text color="black" fontWeight="semibold" ml={[2, 6]}>
                            {link?.fields?.label}
                        </Text>
                    </Flex>
                </Link>
            );
        })}
    </Carousel>
);

export const SearchCarousels: FC<ISearchCarouselsProps> = ({
    customLinksCarousel,
    variant = "default",
    categoryColumns,
}) => {
    const collections = (customLinksCarousel ?? [])
        .map((linkCollection) => {
            const links = (linkCollection?.fields?.links ?? []) as ILink[];
            return { linkCollection, links };
        })
        .filter(({ links }) => links.length > 0);

    return (
        <>
            {collections.map(({ linkCollection, links }, index) => {
                if (variant === "categories") {
                    return (
                        <CategoriesGrid
                            key={linkCollection.sys.id}
                            title={linkCollection?.fields?.title}
                            links={links}
                            columns={categoryColumns}
                        />
                    );
                }

                return (
                    <DefaultCarousel
                        key={linkCollection.sys.id}
                        linkCollection={linkCollection}
                        links={links}
                        index={index}
                    />
                );
            })}
        </>
    );
};
